import React from "react";
import InfiniteTable, { InfiniteTableProps } from "../Display/InfiniteTable";
import { API } from "aws-amplify";
import { IConnectListQuery } from "../../types";
import { Subtract } from "utility-types";

type ConnectedInfiniteTableProps<T> = InfiniteTableProps<T> & {
  queryGenerator: (nextToken: string | null) => { query: any; variables: {} };
  opName: string;
  autoLoadEnabled?: boolean;
};

type InjectedProps<T> = {
  loadMore: () => Promise<void>;
  hasMoreData: boolean;
  loading: boolean;
  data: T[];
};

type State<T> = {
  nextToken: string | null;
  data: T[];
  loading: boolean;
};

class ConnectedInfiniteTable<
  RecordType,
  OpName extends string
> extends React.Component<
  Subtract<ConnectedInfiniteTableProps<RecordType>, InjectedProps<RecordType>>,
  State<RecordType>
> {
  state = {
    nextToken: null,
    loading: false,
    data: [],
  };
  loadMoreRequestsCount: Record<string, number> = {};

  tableDataSeed: string;

  changeDataSeed = (): void => {
    this.tableDataSeed = `${this.props.tableId}-dt-${Math.floor(Math.random() * 10000)}`;
  };

  componentDidMount(): void {
    if (this.props.tableId) {
      // Add listener for table refresh
      window.addEventListener(`refresh#${this.props.tableId}`, async () => {
        this.changeDataSeed();
        this.setState(
          {
            nextToken: null,
            data: [],
          },
          () => {
            this.loadMore();
          }
        );
      });
    }
  }
  loadMore = async (loadMoreRequestId?: number): Promise<void> => {
    this.setState({ loading: true });
    const query = this.props.queryGenerator(this.state.nextToken);
    if (!this.tableDataSeed) {
      this.changeDataSeed();
    }
    const seed = this.tableDataSeed;
    const response = (await API.graphql(query)) as any as IConnectListQuery<
      RecordType,
      OpName
    >;
    if (seed !== this.tableDataSeed) {
      // Seed was changed while loading, we should ignore the data
      this.setState({ loading: false });
      return;
    }
    if (response.data) {
      if (response.data[this.props.opName as OpName]) {
        this.setState(
          (state: State<RecordType>) => ({
            data: [
              ...state.data,
              ...response.data[this.props.opName as OpName].items,
            ],
            nextToken: response.data[this.props.opName as OpName].nextToken,
            loading: false,
          }),
          () => {
            if (
              this.props.autoLoadEnabled &&
              // response.data[this.props.opName as OpName].items.length < 10 &&
              response.data[this.props.opName as OpName].nextToken
            ) {
              // auto load is enabled, we need to check the total count of records
              // loaded in this stream of loads. A stream of loads keeps loading more 
              // until a total of PAGE_SIZE is reached for that stream
              const resultCount = response.data[this.props.opName as OpName].items.length;
              if (loadMoreRequestId) {
                this.loadMoreRequestsCount[loadMoreRequestId] = (this.loadMoreRequestsCount[loadMoreRequestId] ?? 0) + resultCount;
                if (this.loadMoreRequestsCount[loadMoreRequestId] < 20) {
                  this.loadMore(loadMoreRequestId);
                }
              } else {
                // This is the first load more in this sequence
                if (resultCount < 20) {
                  // We need more, so we generate a request id and store the count
                  const lmri = Math.floor(Math.random() * 15);
                  this.loadMoreRequestsCount[lmri] = resultCount;
                  this.loadMore(lmri);
                }
              }
            }
          }
        );
        return;
      }
    }
    this.setState({ loading: false });
  };
  render(): React.ReactElement {
    return (
      <InfiniteTable
        {...this.props}
        loadMore={this.loadMore}
        hasMoreData={this.state.nextToken !== null}
        loading={this.state.loading}
        data={this.state.data}
      />
    );
  }
}

export default ConnectedInfiniteTable;
